/**
 * Provisioning QR helpers — JS equivalent of QRCode.py.
 *
 * Each CTS sensor (a.k.a. "thingy") is paired via the ESP-Jumpstart flow,
 * which scans a QR encoding a JSON payload of shape:
 *
 *   {"ver":"v1","name":"<thingyName>","pop":"<pop>","transport":"softap"}
 *
 * `pop` = base64url( sha256(thingyName + b"\0") ), with trailing `=` stripped.
 *
 * Hashing uses the browser's built-in WebCrypto (`crypto.subtle`) so we don't
 * need a hashing library for ~30 lines of code.
 */

/** Base64-url encode a byte array (no padding). */
function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Compute the ESP-Jumpstart "pop" for a thingy name.
 * Mirrors `__generate_pop` in QRCode.py exactly:
 *   base64url( sha256(thingyName + b"\0") ).rstrip("=")
 */
export async function buildProvisioningPop(
  thingyName: string
): Promise<string> {
  const enc = new TextEncoder();
  const nameBytes = enc.encode(thingyName);
  const input = new Uint8Array(nameBytes.length + 1); // trailing null byte
  input.set(nameBytes, 0);
  // input[nameBytes.length] is already 0
  const digest = await crypto.subtle.digest("SHA-256", input);
  return bytesToBase64Url(new Uint8Array(digest));
}

/**
 * Build the full provisioning JSON payload string for a thingy name.
 * Format mirrors `__generate_json_payload` in QRCode.py — identical character
 * for character so a phone scanning either source gets the same data.
 */
export async function buildProvisioningPayload(
  thingyName: string
): Promise<string> {
  const pop = await buildProvisioningPop(thingyName);
  return `{"ver":"v1","name":"${thingyName}","pop":"${pop}","transport":"softap"}`;
}
